import { useGetIdentity, useIsAuthenticated } from '@refinedev/core';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { UserNotification } from '@/model/General/Notification';
import User from '@/model/General/User';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { clearSentryUserContext, setSentryUserContext } from '@/sentry.config';

interface UserContextType {
  isLoading: boolean;
  userLogged?: User;
  isAuthenticated: boolean;
  userWorkArea?: USER_WORK_AREA;
  canChangeCountry: boolean;
  notifications: UserNotification[];
  updateNotification: (notification: UserNotification) => void;
  markAsReadNotification: (notificationId?: number) => Promise<void>;
  addNotification: (notification: UserNotification) => void;
  unreadNotifications: number;
  pageSizePreference: number;
  setPageSizePreference: (pageSize: number) => void;
  devOptions?: {
    selectedWorkArea?: USER_WORK_AREA;
    handleChangeWorkArea: (workArea: USER_WORK_AREA) => void;
  };
}

const UserContext = createContext<UserContextType>({} as UserContextType);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { userRepository } = useRepositoriesContext();
  const pageSizeStorageKey = 'userPageSize';
  const { data: userLogged, isLoading, refetch: refetchUser } = useGetIdentity<User>();
  const { data } = useIsAuthenticated();
  const isAuthenticated = useMemo(() => (data ? data.authenticated : false), [data]);

  useEffect(() => {
    if (
      isAuthenticated &&
      (!userLogged || (userLogged as unknown as { data: any })?.data) == null
    ) {
      void refetchUser();
    }
  }, [isAuthenticated, refetchUser, userLogged]);

  useEffect(() => {
    // Sincroniza la identidad autenticada en Sentry para correlacionar usuario y fallo.
    if (isAuthenticated && userLogged) {
      setSentryUserContext(userLogged);

      return;
    }

    clearSentryUserContext();
  }, [isAuthenticated, userLogged]);

  //#region Dev options
  const isDev = useMemo(
    () => userLogged?.workArea == USER_WORK_AREA.SOFTWARE,
    [userLogged?.workArea],
  );
  const [selectedWorkArea, setSelectedWorkArea] = useState<USER_WORK_AREA | undefined>();
  const handleChangeWorkArea = useCallback(
    (workArea: USER_WORK_AREA) => {
      if (isDev) setSelectedWorkArea(workArea);
    },
    [isDev],
  );

  useEffect(() => {
    if (userLogged?.workArea && userLogged?.workArea == USER_WORK_AREA.SOFTWARE) {
      setSelectedWorkArea(userLogged?.workArea);
    }
  }, [userLogged?.workArea]);
  //#endregion

  //#region Table page size preference
  const getStoredPageSizePreference = useCallback(() => {
    if (typeof window === 'undefined') return undefined;

    const stored = localStorage.getItem(pageSizeStorageKey);
    const parsed = stored ? parseInt(stored, 10) : NaN;

    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }, [pageSizeStorageKey]);
  const [pageSizePreference, setPageSizePreference] = useState<number>(
    () => getStoredPageSizePreference() ?? 20,
  );

  useEffect(() => {
    const stored = getStoredPageSizePreference();

    if (stored && stored !== pageSizePreference) {
      setPageSizePreference(stored);
    }
  }, [getStoredPageSizePreference, pageSizePreference]);

  const handleSetPageSizePreference = useCallback(
    (pageSize: number) => {
      setPageSizePreference(pageSize);

      if (typeof window !== 'undefined') {
        localStorage.setItem(pageSizeStorageKey, pageSize.toString());
      }
    },
    [pageSizeStorageKey],
  );
  //#endregion
  const userWorkArea = useMemo(
    () => (selectedWorkArea ? selectedWorkArea : userLogged?.workArea),
    [selectedWorkArea, userLogged?.workArea],
  );
  const canChangeCountry = useMemo(
    () =>
      userWorkArea === USER_WORK_AREA.SOFTWARE || userWorkArea === USER_WORK_AREA.CUSTOMER_SERVICE,
    [userWorkArea],
  );
  //#region notifications
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState<number>(0);

  useEffect(() => {
    if (userLogged?.notificationsUnread !== undefined) {
      setUnreadNotifications(userLogged.notificationsUnread);

      return;
    }

    setUnreadNotifications(0);
  }, [userLogged?.notificationsUnread]);

  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);

      return;
    }

    void userRepository.getUserNotification().then(data => {
      setNotifications(data);
    });
  }, [userRepository, isAuthenticated]);

  const updateNotification = useCallback((notification: UserNotification) => {
    if (!notification?.id) return;

    setNotifications(prev =>
      prev.map(item => (item.id === notification.id ? { ...item, ...notification } : item)),
    );
  }, []);
  const markAsReadNotification = useCallback(
    async (notificationId?: number) => {
      if (!notificationId) return;

      const updated = await userRepository.markNotificationRead(notificationId);

      if (updated) {
        updateNotification(updated);

        if (unreadNotifications > 0) {
          setUnreadNotifications(unreadNotifications - 1);
        }
      }
    },
    [updateNotification, userRepository, unreadNotifications],
  );
  const addNotification = useCallback((notification: UserNotification) => {
    setNotifications(prev => [notification, ...prev]);
    setUnreadNotifications(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const baseTitle = document.title.replace(/^\(\d+\)\s*/, '');
    const prefix = unreadNotifications > 0 ? `(${unreadNotifications}) ` : '';

    document.title = `${prefix}${baseTitle}`;
  }, [unreadNotifications]);
  //#endregion

  return (
    <UserContext.Provider
      value={{
        isLoading,
        userLogged,
        isAuthenticated,
        notifications,
        updateNotification,
        addNotification,
        unreadNotifications,
        markAsReadNotification,
        userWorkArea,
        canChangeCountry,
        pageSizePreference,
        setPageSizePreference: handleSetPageSizePreference,
        devOptions: isDev ? { selectedWorkArea, handleChangeWorkArea } : undefined,
      }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => useContext(UserContext);
